import type { JSONContent } from "@tiptap/core";
import { formatLocalDate } from "../../../../shared/local-date";
import { meetingNoteTextFromJson } from "./meeting-note-editor";

/**
 * Pure meeting-note types and constants shared by the meeting-notes UI.
 * Ported from the legacy `lib/v2/meeting-note-types.ts`.
 */

export const DEFAULT_MEETING_NOTE_TEMPLATE_KEY = "default_meeting_note";
export const DEFAULT_MEETING_NOTE_TEMPLATE_VERSION = 1;

export type MeetingNoteBodyJson = JSONContent;

export const DEFAULT_MEETING_NOTE_BODY_JSON: MeetingNoteBodyJson = {
  type: "doc",
  content: [
    {
      type: "heading",
      attrs: { level: 3 },
      content: [{ type: "text", text: "Agenda" }],
    },
    { type: "paragraph" },
    {
      type: "heading",
      attrs: { level: 3 },
      content: [{ type: "text", text: "Notes" }],
    },
    { type: "paragraph" },
    {
      type: "heading",
      attrs: { level: 3 },
      content: [{ type: "text", text: "Action Items" }],
    },
    { type: "paragraph" },
  ],
};

export function defaultMeetingNoteBodyJson(): MeetingNoteBodyJson {
  return JSON.parse(JSON.stringify(DEFAULT_MEETING_NOTE_BODY_JSON)) as MeetingNoteBodyJson;
}

export function isMeetingNoteBodyJson(
  value: unknown,
): value is MeetingNoteBodyJson {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value as Record<string, unknown>).type === "doc"
  );
}

/** A single meeting note, projected for the UI with user names resolved. */
export interface MeetingNoteData {
  id: string;
  title: string | null;
  meetingDate: string;
  fathomUrl: string | null;
  bodyJson: MeetingNoteBodyJson;
  templateKey: string | null;
  templateVersion: number | null;
  lastSharedAt: string | null;
  createdAt: string;
  updatedAt: string;
  supervisorUserId: string;
  supervisorName: string;
  supervisorEmail: string;
  authorUserId: string;
  authorName: string;
  shareHistory: MeetingNoteShareHistoryItem[];
}

export interface MeetingNoteShareHistoryItem {
  id: string;
  senderName: string;
  recipientName: string;
  status: "attempted" | "sent" | "failed";
  createdAt: string;
  sentAt: string | null;
}

export type MeetingNoteShareRecipientKey = string;

export interface MeetingNoteShareRecipientOption {
  key: MeetingNoteShareRecipientKey;
  name: string;
}

export { todayIsoDate } from "../../../../shared/local-date";

export function meetingNoteDisplayTitle(
  note: Pick<MeetingNoteData, "title" | "meetingDate">,
): string {
  if (note.title) return note.title;
  const formatted = formatLocalDate(note.meetingDate, "shortWithYear");
  return `Meeting note - ${formatted}`;
}

export function meetingNotePlainText(bodyJson: MeetingNoteBodyJson | unknown): string {
  if (!isMeetingNoteBodyJson(bodyJson)) return "";
  return meetingNoteTextFromJson(bodyJson);
}

export function hasMeetingNoteBodyContent(
  bodyJson: MeetingNoteBodyJson | unknown,
): boolean {
  const text = meetingNotePlainText(bodyJson);
  const defaultTemplateText = meetingNoteTextFromJson(
    DEFAULT_MEETING_NOTE_BODY_JSON,
  );
  return !!text && text !== defaultTemplateText;
}

export function meetingNotePreview(
  bodyJson: MeetingNoteBodyJson,
  maxLength = 140,
): string {
  const text = meetingNotePlainText(bodyJson);
  if (text.length <= maxLength) return text;
  const clipped = text.slice(0, Math.max(0, maxLength - 3)).trimEnd();
  const lastSpace = clipped.lastIndexOf(" ");
  return `${lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped}...`;
}
